/**
 * The middle step of every private-media upload (docs/05-admin/03 §6.14): presign → **PUT** →
 * confirm. The PUT is a bare `fetch` straight to storage — the presigned URL carries its own
 * signature, and our bearer token on it would be both useless and a leak to the storage host.
 *
 * A refusal throws an `ApiError`, not a bare `Error`: the toast reads `detail`, and a generic
 * "something went wrong" would hide that the bytes never landed — the one fact that tells an
 * operator to try the file again. What happens to the half-made record differs per surface, so
 * the caller says it in `afterRefusal`.
 */
import { ApiError } from './client';

export interface PresignedPut {
  url: string;
  method: string;
  headers: { [key: string]: string };
}

export async function putPresigned(
  target: PresignedPut,
  file: File,
  afterRefusal = 'Nothing was recorded.',
): Promise<void> {
  let put: Response;
  try {
    put = await fetch(target.url, { method: target.method, headers: target.headers, body: file });
  } catch {
    // No answer at all: a host the browser cannot resolve, or storage CORS refusing the origin.
    // The host is the one fact that tells ops which, so the message names it.
    throw new ApiError({
      status: 0,
      code: 'storage_unreachable',
      detail: `Could not reach storage at ${new URL(target.url).host}. ${afterRefusal}`,
    });
  }
  if (!put.ok) {
    throw new ApiError({
      status: put.status,
      code: 'upload_failed',
      detail: `Storage refused the upload (${put.status}). ${afterRefusal}`,
    });
  }
}
